import { resolveBuildConfig, type BuildConfig } from '@shared/build-config';
import { localizePath } from '@shared/locales';
import { withReferral } from './referral';

/**
 * This build's configuration (see src/shared/build-config.ts). vite.config.ts
 * validates the same values before the build starts, so by the time this runs
 * they are known to be well formed. Under Vitest nothing is injected and the
 * local defaults apply.
 */
export const buildConfig: BuildConfig = resolveBuildConfig({
  APP_ENV: import.meta.env.LANDMATE_APP_ENV,
  SITE_URL: import.meta.env.LANDMATE_SITE_URL,
  APP_ORIGIN: import.meta.env.LANDMATE_APP_ORIGIN,
  APP_LINKS_ENABLED: import.meta.env.LANDMATE_APP_LINKS_ENABLED,
});

export interface AppLinks {
  /** False until APP_ORIGIN is configured and APP_LINKS_ENABLED is on: the app is not open to the public. */
  available: boolean;
  signup: string;
  login: string;
}

/**
 * Where "Get started" and "Sign in" go. With an app origin AND the links
 * switched on (APP_LINKS_ENABLED; off in production until sign-up opens) they
 * go to the app's sign-up and sign-in pages (cross-repo contract: the Landmate
 * web app serves /sign-up and /sign-in). A partner referral code the visitor
 * arrived with (src/web/lib/referral.ts) is forwarded on the sign-up link as
 * `?ref=CODE`; while the links are off it is dropped, since there is no
 * sign-up to credit. Otherwise they go to this site's
 * /get-started page, which says the app is not available yet — never to a
 * domain that does not answer, and never past copy that says sign-up is closed.
 */
export function appLinks(
  config: Pick<BuildConfig, 'appOrigin' | 'appLinksEnabled'>,
  locale: string,
  referral: string | null = null,
): AppLinks {
  if (!config.appOrigin || !config.appLinksEnabled) {
    const local = localizePath('/get-started', locale);
    return { available: false, signup: local, login: local };
  }
  return {
    available: true,
    signup: withReferral(`${config.appOrigin}/sign-up`, referral),
    login: `${config.appOrigin}/sign-in`,
  };
}
